// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Button, DataTableCell, DataTableRow, LoadingSpinner, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { sharedDateTimeOptions } from "@vanmeter/XDL/constants";
import { parseBoolean } from "@vanmeter/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    // XNG-Changes
    isReordering: state.pages.orderHistory.isReordering,
    isUpdatingCart: state.context.isUpdatingCart,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
});

// <XNG-Changes>
const mapDispatchToProps = {
    reorder,
};

interface PopsObj {
    show: boolean;
    title: string;
}
export interface RecentOrderTableProperties {
    orderNumber: PopsObj;
    date: PopsObj;
    orderTotal: PopsObj;
    status: PopsObj;
    shipTo: PopsObj;
    pONumber: PopsObj;
    reorderProducts: PopsObj;
    releaseNumber: PopsObj;
}
// </XNG-Changes>

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // XNG-Changes
    tableProperties?: RecentOrderTableProperties;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // XNG-Changes
    clickableCell?: LinkPresentationProps;
    dataCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    totalCells?: DataTableCellProps;
    actionsCells?: DataTableCellProps;
    reorderLink?: LinkPresentationProps;
    reorderLinkSpinner?: LoadingSpinnerProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0 20px;
            max-width: 500px;
        `,
    },
    container: { gap: 5 },
    pageTypeLinkGridItem: { width: 12 },
    orderNumberGridItem: { width: 6 },
    statusGridItem: { width: 6 },
    webOrderNumberGridItem: { width: 6 },
    totalGridItem: { width: 6 },
};

const OrderSummaryCard: FC<Props> = ({
    language,
    order,
    orderSettings,
    extendedStyles,
    // XNG-Changes
    isReordering,
    isUpdatingCart,
    reorder,
    showAddToCartConfirmationDialog,
    history,
    isVmiOrderHistoryPage,
    tableProperties,
    showPoNumber,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    // XNG-Changes
    const toaster = useToaster();
    const { orderDate, customerPO, statusDisplay, orderGrandTotalDisplay } = order;
    // XNG-Changes : from classic to match orderhistory
    const [reorderedNumbers, setReorderedNumbers] = useState<Record<string, boolean>>({});

    const date = getLocalizedDateTime({
        dateTime: orderDate,
        language,
        options: sharedDateTimeOptions,
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    // XNG-Changes:from classic
    const shipTo = order.stCompanyName;

    // XNG-Changes
    const onReorderSuccess = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    // XNG-Changes
    const onReorderError = (message: string) => {
        toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };

    // XNG-Changes
    const reorderClick = (orderNumber: string, linkOrderNumber: string, isVmiOrder: boolean) => {
        setReorderedNumbers({ ...reorderedNumbers, [orderNumber]: true });

        reorder({
            orderNumber,
            onSuccess: () => onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder),
            onError: message => onReorderError(message),
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.();
                }
            },
        });
    };

    // XNG-Changes
    const canReorder = parseBoolean(order.properties?.canReorder);
    const isOrderReordering = isReordering[orderNumber];

    // XNG-Changes
    return (
        <DataTableRow>
            {tableProperties?.date.show && (
                <DataTableCell
                    className="page-type-link-grid-item"
                    {...{ header: tableProperties.date.title }}
                    {...styles.clickableCell}
                    tight={true}
                >
                    <OrderDetailPageTypeLink
                        title={date}
                        orderNumber={orderNumber}
                        isVmiOrder={!!order.vmiLocationId}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                </DataTableCell>
            )}
            {tableProperties?.orderNumber.show && (
                <DataTableCell
                    className="page-type-link-grid-item"
                    data-test-selector="orderSummaryCard_Date"
                    {...{ header: tableProperties.orderNumber.title }}
                    {...styles.clickableCell}
                    tight={true}
                >
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={orderNumber}
                        isVmiOrder={!!order.vmiLocationId}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                </DataTableCell>
            )}
            {tableProperties?.pONumber.show && showPoNumber && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.pONumber.title }}>
                    {customerPO}
                </DataTableCell>
            )}
            {tableProperties?.releaseNumber.show && (
                <DataTableCell {...styles.dataCells} {...{ header: tableProperties.releaseNumber.title }}>
                    {order.properties?.releaseNumber}
                </DataTableCell>
            )}
            {tableProperties?.shipTo.show && (
                <DataTableCell {...styles.shipToCells} {...{ header: tableProperties.shipTo.title }}>
                    {shipTo}
                </DataTableCell>
            )}
            {tableProperties?.status.show && (
                <DataTableCell
                    {...styles.dataCells}
                    {...{ header: tableProperties.status.title }}
                    className="status-grid-item"
                >
                    {statusDisplay}
                </DataTableCell>
            )}
            {tableProperties?.orderTotal.show && (
                <DataTableCell
                    {...styles.dataCells}
                    {...{ header: tableProperties.orderTotal.title }}
                    className="total-grid-item"
                >
                    {orderGrandTotalDisplay}
                </DataTableCell>
            )}
            {tableProperties?.reorderProducts.show && (
                <DataTableCell
                    {...styles.actionsCells}
                    {...{ header: tableProperties.reorderProducts.title }}
                    className="total-grid-item"
                >
                    {/* XNG-Changes:from classic */}
                    {canReorder &&
                        (isOrderReordering ? (
                            <LoadingSpinner {...styles.reorderLinkSpinner} />
                        ) : (
                            <Link
                                disabled={
                                    Object.keys(isReordering).length > 0 || isUpdatingCart || reorderedNumbers[order.id]
                                }
                                {...styles.reorderLink}
                                onClick={() => reorderClick(orderNumber, orderNumber, !!order.vmiLocationId)}
                                data-test-selector="orderHistoryTable_Reorder"
                                aria-label="Reorder"
                            >
                                {translate("Reorder")}
                            </Link>
                        ))}
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(OrderSummaryCard));
